import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Calendar, Clock, Users, BookOpen, BarChart3, Save, FileText, Printer, CheckCircle2, MessageSquare, Bell, Upload, Download, Trash2, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { useLang } from '@/contexts/LangContext';
import useSystemNotifications from '@/hooks/useSystemNotifications';
import { formatDate, formatMoney, formatNumber } from '@/lib/utils';

function toDateInputValue(date) {
  return date.toISOString().split('T')[0];
}

function getDefaultHoursRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    date_from: toDateInputValue(start),
    date_to: toDateInputValue(now),
  };
}

function apiErrorMessage(error, fallback) {
  return error?.response?.data?.error || error?.response?.data?.message || fallback;
}

const SESSION_STATE_LABEL_KEYS = {
  scheduled: 'teacher.state_scheduled',
  in_progress: 'teacher.state_in_progress',
  complete: 'teacher.state_complete',
  postponed: 'teacher.state_postponed',
  holiday: 'teacher.state_holiday',
  cancelled: 'teacher.state_cancelled',
};

const SESSION_STATE_VARIANTS = {
  complete: 'default',
  cancelled: 'destructive',
  postponed: 'secondary',
  holiday: 'secondary',
};

const CLASS_TYPE_LABEL_KEYS = {
  group: 'teacher.class_type_group',
  mini_group: 'teacher.class_type_mini_group',
  vip: 'teacher.class_type_vip',
};

const CLASS_PLAN_STATUS_KEYS = {
  submitted: 'teacher.class_plan_submitted',
  missing: 'teacher.class_plan_missing',
};

const ATTENDANCE_STATUS_KEYS = {
  present: 'teacher.present',
  absent: 'teacher.absent',
  late: 'teacher.late',
};

const SCHEDULE_REQUEST_STATUS_KEYS = {
  pending_teacher: 'teacher.status_pending_teacher',
  change_requested: 'teacher.status_change_requested',
  accepted: 'teacher.status_accepted',
  rejected: 'teacher.status_rejected',
};

function StudentChips({ students, names, max = 4 }) {
  const rawStudents = Array.isArray(students) ? students : [];
  const rawNames = Array.isArray(names) ? names : [];
  const normalized = rawStudents.length > 0
    ? rawStudents.map((student, index) => ({
        id: student.id || student.uuid || `${student.name || 'student'}-${index}`,
        name: student.name || String(student || ''),
      }))
    : rawNames.map((name, index) => ({
        id: `${name}-${index}`,
        name,
      }));
  const visible = normalized.filter((student) => student.name).slice(0, max);
  const hiddenCount = Math.max(0, normalized.length - visible.length);

  if (visible.length === 0) {
    return <span className="text-xs text-muted-foreground">-</span>;
  }

  return (
    <div className="flex max-w-[280px] flex-wrap gap-1.5">
      {visible.map((student) => (
        <Badge key={student.id} variant="outline" className="rounded-full border-primary/20 bg-primary/5 px-2 py-0.5 text-[11px] font-medium text-primary">
          {student.name}
        </Badge>
      ))}
      {hiddenCount > 0 ? (
        <Badge variant="secondary" className="rounded-full px-2 py-0.5 text-[11px]">
          +{hiddenCount}
        </Badge>
      ) : null}
    </div>
  );
}

function packageProgressLabel(session, t) {
  const total = Number(session.package_total_sessions || 0);
  const completed = Number(session.package_completed_sessions || 0);
  const remaining = Number(session.package_remaining_sessions || Math.max(0, total - completed));
  if (!total) return '';
  if (remaining <= 0) return t('teacher.package_completed');
  return `${completed}/${total} ${t('teacher.progress_completed')} \u2022 ${t('teacher.progress_remaining')} ${remaining}`;
}

function ClassTypeBadge({ value, label, t }) {
  const classType = value || 'group';
  const key = CLASS_TYPE_LABEL_KEYS[classType] || CLASS_TYPE_LABEL_KEYS.group;
  const text = key ? t(key) : label;
  const className = classType === 'vip'
    ? 'border-amber-300 bg-amber-50 text-amber-700'
    : classType === 'mini_group'
      ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
      : 'border-emerald-300 bg-emerald-50 text-emerald-700';
  return (
    <Badge variant="outline" className={`text-xs ${className}`}>
      {text}
    </Badge>
  );
}

export default function TeacherDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t, lang, isRTL } = useLang();
  const [schedule, setSchedule] = useState([]);
  const [groups, setGroups] = useState([]);
  const [hours, setHours] = useState(null);
  const [teacherSessions, setTeacherSessions] = useState([]);
  const [hoursRange, setHoursRange] = useState(getDefaultHoursRange);
  const [scheduleRequests, setScheduleRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState({});
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [loadingHours, setLoadingHours] = useState(false);
  const [processingRequestId, setProcessingRequestId] = useState(null);
  const [selectedPlanSession, setSelectedPlanSession] = useState(null);
  const [selectedClassDetails, setSelectedClassDetails] = useState(null);
  const [loadingClassDetails, setLoadingClassDetails] = useState(false);
  const [activeClassReferenceUuid, setActiveClassReferenceUuid] = useState('');
  const [classPlanForm, setClassPlanForm] = useState({
    lesson_plan: '',
    topics_taught: '',
    materials_notes: '',
    resource_links: '',
    attachments: [],
  });
  const [classPlanFiles, setClassPlanFiles] = useState([]);
  const [savingClassPlan, setSavingClassPlan] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  const {
    notifications,
    unreadCount,
    permissionState,
    requestPermission,
    markDone,
  } = useSystemNotifications({ enabled: Boolean(user) });

  const translateSessionState = (state) => {
    const key = SESSION_STATE_LABEL_KEYS[state];
    return key ? t(key) : (state || '-');
  };

  const translateClassPlanStatus = (status) => {
    const normalized = status || 'missing';
    const key = CLASS_PLAN_STATUS_KEYS[normalized];
    return key ? t(key) : normalized;
  };

  const translateAttendanceStatus = (status) => {
    const normalized = status || 'present';
    const key = ATTENDANCE_STATUS_KEYS[normalized];
    return key ? t(key) : normalized;
  };

  const translateScheduleRequestStatus = (status) => {
    const key = SCHEDULE_REQUEST_STATUS_KEYS[status];
    return key ? t(key) : (status || '-');
  };

  const hydrateAttendance = (sessionData = []) => {
    const att = {};
    sessionData.forEach((session) => {
      (session.students || []).forEach((student) => {
        att[`${session.id}-${student.id}`] = student.status || 'present';
      });
    });
    setAttendance(att);
  };

  const refreshScheduleData = async () => {
    const [scheduleRes, requestRes] = await Promise.all([
      api.get('/v1/teacher/schedule'),
      api.get('/v1/teacher/schedule-requests'),
    ]);
    setSchedule(Array.isArray(scheduleRes.data) ? scheduleRes.data : []);
    setScheduleRequests(Array.isArray(requestRes.data) ? requestRes.data : []);
  };

  const refreshTeacherSessions = async (range = hoursRange) => {
    const response = await api.get('/v1/teacher/sessions', { params: range });
    const sessionData = Array.isArray(response.data?.sessions) ? response.data.sessions : [];
    setTeacherSessions(sessionData);
    hydrateAttendance(sessionData);
  };

  const openClassDetails = async (classReferenceUuid) => {
    if (!classReferenceUuid) return;
    setActiveClassReferenceUuid(classReferenceUuid);
    setLoadingClassDetails(true);
    try {
      const response = await api.get(`/v1/teacher/classes/${classReferenceUuid}/details`);
      setSelectedClassDetails(response.data || null);
    } catch {
      toast.error(t('teacher.class_details_load_failed'));
    } finally {
      setLoadingClassDetails(false);
    }
  };

  useEffect(() => {
    if (!user && !authLoading) { navigate('/login'); return; }
    if (!user) return;
    Promise.allSettled([
      api.get('/v1/teacher/schedule'),
      api.get('/v1/teacher/groups'),
      api.get('/v1/teacher/hours', { params: getDefaultHoursRange() }),
      api.get('/v1/teacher/sessions', { params: getDefaultHoursRange() }),
      api.get('/v1/teacher/schedule-requests'),
    ]).then(([sRes, gRes, hRes, sessionRes, rRes]) => {
      const scheduleData = sRes.status === 'fulfilled' && Array.isArray(sRes.value?.data) ? sRes.value.data : [];
      const groupData = gRes.status === 'fulfilled' && Array.isArray(gRes.value?.data) ? gRes.value.data : [];
      const hoursData = hRes.status === 'fulfilled' ? hRes.value?.data : null;
      const sessionData = sessionRes.status === 'fulfilled' && Array.isArray(sessionRes.value?.data?.sessions) ? sessionRes.value.data.sessions : [];
      const requestData = rRes.status === 'fulfilled' && Array.isArray(rRes.value?.data) ? rRes.value.data : [];

      setSchedule(scheduleData);
      setGroups(groupData);
      setHours(hoursData);
      setTeacherSessions(sessionData);
      setScheduleRequests(requestData);
      hydrateAttendance(sessionData);

      if (rRes.status === 'rejected') {
        toast.error(t('teacher.schedule_requests_load_failed'));
      }
    }).finally(() => setLoading(false));
  }, [user, authLoading, navigate, lang, t]);

  const loadHours = async () => {
    setLoadingHours(true);
    try {
      const [hoursResponse, sessionsResponse] = await Promise.all([
        api.get('/v1/teacher/hours', { params: hoursRange }),
        api.get('/v1/teacher/sessions', { params: hoursRange }),
      ]);
      const sessionData = Array.isArray(sessionsResponse.data?.sessions) ? sessionsResponse.data.sessions : [];
      setHours(hoursResponse.data);
      setTeacherSessions(sessionData);
      hydrateAttendance(sessionData);
    } catch {
      toast.error(t('alerts.generic_error'));
    } finally {
      setLoadingHours(false);
    }
  };

  const updateAttendanceStatus = (sessionId, studentId, status) => {
    setAttendance(prev => ({ ...prev, [`${sessionId}-${studentId}`]: status }));
  };

  const saveAttendance = async (session) => {
    setSavingAttendance(true);
    try {
      const students = (session.students || []).map(s => ({
        student_id: s.id,
        name: s.name,
        status: attendance[`${session.id}-${s.id}`] || s.status || 'present',
        note: ''
      }));
      const response = await api.post(`/v1/teacher/sessions/${session.id}/attendance`, { students });
      if (response.data?.session) {
        const updatedSession = response.data.session;
        setTeacherSessions((current) => current.map((item) => (
          item.id === session.id ? updatedSession : item
        )));
        setAttendance((current) => {
          const next = { ...current };
          (updatedSession.students || []).forEach((student) => {
            next[`${updatedSession.id}-${student.id}`] = student.status || 'present';
          });
          return next;
        });
      }
      toast.success(t('alerts.attendance_saved'));
      await Promise.all([loadHours(), refreshScheduleData().catch(() => {})]);
    } catch {
      toast.error(t('alerts.attendance_save_failed'));
    } finally {
      setSavingAttendance(false);
    }
  };

  const printSalarySlip = async () => {
    setLoadingHours(true);
    try {
      const response = await api.get('/v1/teacher/salary-slip', { params: hoursRange });
      setHours(response.data);
      window.print();
    } catch {
      toast.error(t('alerts.generic_error'));
    } finally {
      setLoadingHours(false);
    }
  };

  const acceptScheduleRequest = async (requestId) => {
    setProcessingRequestId(requestId);
    try {
      const response = await api.post(`/v1/teacher/schedule-requests/${requestId}/accept`, {});
      toast.success(t('teacher.schedule_accepted'));
      if (response.data?.request) {
        setScheduleRequests((items) => items.map((item) => (
          item.id === requestId ? { ...item, ...response.data.request } : item
        )));
      }
      try {
        await refreshScheduleData();
      } catch {
        toast.warning(t('teacher.refresh_after_accept_failed') || t('alerts.generic_error'));
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, t('alerts.generic_error')));
    } finally {
      setProcessingRequestId(null);
    }
  };

  const requestScheduleChange = async (requestId) => {
    const notes = window.prompt(t('teacher.schedule_change_prompt'));
    if (notes === null) return;
    setProcessingRequestId(requestId);
    try {
      await api.post(`/v1/teacher/schedule-requests/${requestId}/request-change`, { notes });
      toast.success(t('teacher.change_request_sent'));
      await refreshScheduleData();
    } catch (error) {
      toast.error(apiErrorMessage(error, t('alerts.generic_error')));
    } finally {
      setProcessingRequestId(null);
    }
  };

  const openClassPlan = async (session) => {
    setSelectedPlanSession(session);
    setClassPlanFiles([]);
    try {
      const response = await api.get(`/v1/teacher/sessions/${session.id}/class-plan`);
      const plan = response.data || {};
      setClassPlanForm({
        lesson_plan: plan.lesson_plan || '',
        topics_taught: plan.topics_taught || '',
        materials_notes: plan.materials_notes || '',
        resource_links: plan.resource_links || '',
        attachments: Array.isArray(plan.attachments) ? plan.attachments : [],
      });
    } catch {
      toast.error(t('alerts.generic_error'));
    }
  };

  const updateClassPlanField = (field, value) => {
    setClassPlanForm(prev => ({ ...prev, [field]: value }));
  };

  const addClassPlanFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setClassPlanFiles(prev => [...prev, ...incoming]);
  };

  const removeSelectedClassPlanFile = (index) => {
    setClassPlanFiles(prev => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const saveClassPlan = async () => {
    if (!selectedPlanSession) return;
    setSavingClassPlan(true);
    try {
      const formData = new FormData();
      formData.append('lesson_plan', classPlanForm.lesson_plan || '');
      formData.append('topics_taught', classPlanForm.topics_taught || '');
      formData.append('materials_notes', classPlanForm.materials_notes || '');
      formData.append('resource_links', classPlanForm.resource_links || '');
      classPlanFiles.forEach((file) => formData.append('files', file));
      const response = await api.post(`/v1/teacher/sessions/${selectedPlanSession.id}/class-plan`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('teacher.class_plan_saved'));
      const plan = response.data?.class_plan || {};
      if (response.data?.session) {
        setSelectedPlanSession(response.data.session);
      }
      setClassPlanForm({
        lesson_plan: plan.lesson_plan || '',
        topics_taught: plan.topics_taught || '',
        materials_notes: plan.materials_notes || '',
        resource_links: plan.resource_links || '',
        attachments: Array.isArray(plan.attachments) ? plan.attachments : [],
      });
      setClassPlanFiles([]);
      await Promise.all([refreshTeacherSessions(), loadHours()]);
    } catch {
      toast.error(t('alerts.generic_error'));
    } finally {
      setSavingClassPlan(false);
    }
  };

  const deleteClassPlanAttachment = async (attachmentId) => {
    if (!selectedPlanSession || !attachmentId) return;
    setDeletingAttachmentId(attachmentId);
    try {
      const response = await api.delete(`/v1/teacher/sessions/${selectedPlanSession.id}/class-plan/attachments/${attachmentId}`);
      toast.success(t('teacher.attachment_deleted'));
      const plan = response.data?.class_plan || {};
      if (response.data?.session) {
        setSelectedPlanSession(response.data.session);
      }
      setClassPlanForm(prev => ({
        ...prev,
        attachments: Array.isArray(plan.attachments) ? plan.attachments : [],
      }));
      await Promise.all([refreshTeacherSessions(), loadHours()]);
    } catch {
      toast.error(t('alerts.generic_error'));
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const downloadClassPlanAttachment = async (attachment) => {
    if (!attachment?.url) return;
    try {
      const response = await api.get(attachment.url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], {
        type: attachment.mimetype || 'application/octet-stream',
      }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = attachment.name || 'class-plan-attachment';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error(t('alerts.generic_error'));
    }
  };

  const enableDesktopNotifications = async () => {
    const status = await requestPermission();
    if (status === 'granted') {
      toast.success(t('teacher.desktop_notifications_enabled'));
    } else if (status === 'denied') {
      toast.error(t('teacher.browser_notifications_blocked'));
    } else if (status === 'unsupported') {
      toast.error(t('teacher.browser_notifications_unsupported'));
    }
  };

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-pulse space-y-4 w-full max-w-4xl px-4">
        <div className="h-8 bg-muted rounded w-1/3" />
        <div className="grid grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-28 bg-muted rounded-2xl" />)}
        </div>
      </div>
    </div>
  );

  const showExplore = schedule.length === 0 && groups.length === 0;
  const salaryAmount = Number(hours?.total_salary_month || 0);
  const rateBreakdown = Array.isArray(hours?.rate_breakdown) ? hours.rate_breakdown : [];
  const allowance = Number(hours?.transportation_allowance || 0);
  const maxWeeklyHours = Math.max(1, ...(hours?.weekly_breakdown?.map((item) => Number(item.hours || 0)) || []));

  const renderClassDetailsPanel = () => {
    if (loadingClassDetails) {
      return (
        <Card className="rounded-2xl">
          <CardContent className="p-5 text-sm text-muted-foreground">{t('teacher.loading_class_details')}</CardContent>
        </Card>
      );
    }
    if (!selectedClassDetails) {
      return null;
    }
    const counters = selectedClassDetails.session_counters || {};
    const statusCounts = counters.status_counts || {};
    return (
      <Card className="rounded-2xl">
        <CardContent className="space-y-4 p-5">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">{selectedClassDetails.class_name || '-'}</h3>
            <ClassTypeBadge value={selectedClassDetails?.course?.class_type} label={selectedClassDetails?.course?.class_type_label} t={t} />
          </div>
          <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
            <p>{t('teacher.course')}: <span className="font-medium text-foreground">{selectedClassDetails?.course?.name || '-'}</span></p>
            <p>{t('teacher.mode')}: <span className="font-medium text-foreground">{selectedClassDetails?.schedule?.mode_label || '-'}</span></p>
            <p>{t('teacher.days')}: <span className="font-medium text-foreground">{selectedClassDetails?.schedule?.days || '-'}</span></p>
            <p>{t('teacher.time')}: <span className="font-medium text-foreground">{selectedClassDetails?.schedule?.time || '-'}</span></p>
            <p>{t('teacher.start_after')}: <span className="font-medium text-foreground">{selectedClassDetails?.schedule?.start_after_date || '-'}</span></p>
            <p>{t('teacher.progress')}: <span className="font-medium text-foreground">{counters.progress_label || '-'}</span></p>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="rounded-lg border p-3 text-xs">
              <p className="text-muted-foreground">{t('teacher.total')}</p>
              <p className="mt-1 text-base font-semibold">{counters.total_sessions || 0}</p>
            </div>
            <div className="rounded-lg border p-3 text-xs">
              <p className="text-muted-foreground">{t('teacher.completed')}</p>
              <p className="mt-1 text-base font-semibold">{counters.completed_sessions || 0}</p>
            </div>
            <div className="rounded-lg border p-3 text-xs">
              <p className="text-muted-foreground">{t('teacher.remaining')}</p>
              <p className="mt-1 text-base font-semibold">{counters.remaining_sessions || 0}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(statusCounts).map(([stateKey, value]) => (
              <Badge key={stateKey} variant="outline" className="text-xs">
                {translateSessionState(stateKey)}: {value}
              </Badge>
            ))}
          </div>
          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">{t('teacher.students')}</p>
            <StudentChips students={selectedClassDetails.students || []} />
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div data-testid="teacher-dashboard" className="bf-rtl-scope py-6 sm:py-8" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-semibold">{t("teacher.welcome")} <span className="text-primary">{user?.first_name}</span></h1>
            <p className="text-sm text-muted-foreground mt-1">{t('teacher.overview')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" className="rounded-lg gap-2" onClick={enableDesktopNotifications}>
              <Bell className="h-4 w-4" />
              {permissionState === 'granted' ? t('teacher.notifications_on') : t('teacher.enable_notifications')}
              {unreadCount > 0 ? <Badge variant="secondary">{unreadCount}</Badge> : null}
            </Button>
            <Badge variant="outline" className="rounded-lg w-fit">{user?.role}</Badge>
          </div>
        </div>

        {showExplore && (
          <Card className="rounded-2xl mb-6 border-primary/20 bg-primary/5">
            <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{t('teacher.explore_title')}</p>
                  <p className="text-xs text-muted-foreground">{t('teacher.explore_subtitle')}</p>
                </div>
              </div>
              <Link to="/courses">
                <Button className="rounded-xl" size="sm">{t('teacher.explore_action')}</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          <Card className="rounded-2xl">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">{t('teacher.today_classes')}</span>
                <Calendar className="h-4 w-4 text-primary" />
              </div>
              <p className="text-2xl font-bold">{schedule.length}</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">{t('teacher.total_groups')}</span>
                <Users className="h-4 w-4 text-primary" />
              </div>
              <p className="text-2xl font-bold">{groups.length}</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">{t('teacher.hours_month')}</span>
                <Clock className="h-4 w-4 text-primary" />
              </div>
              <p className="text-2xl font-bold">{hours?.total_hours_month || 0}</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">{t('teacher.classes_month')}</span>
                <BarChart3 className="h-4 w-4 text-primary" />
              </div>
              <p className="text-2xl font-bold">{hours?.total_classes_month || 0}</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="schedule" className="space-y-6">
          <TabsList className="bf-tabs-list rounded-xl bg-muted/50">
            <TabsTrigger value="schedule" className="rounded-lg">{t('teacher.my_classes')}</TabsTrigger>
            <TabsTrigger value="sessions" className="rounded-lg">{t('teacher.sessions')}</TabsTrigger>
            <TabsTrigger value="schedule_requests" className="rounded-lg">{t('teacher.schedule_requests')}</TabsTrigger>
            <TabsTrigger value="attendance" className="rounded-lg">{t('teacher.attendance')}</TabsTrigger>
            <TabsTrigger value="hours" className="rounded-lg">{t('teacher.hours')}</TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-lg">{t('dashboard.notifications')} {unreadCount > 0 ? `(${unreadCount})` : ''}</TabsTrigger>
          </TabsList>

          <TabsContent value="schedule" data-testid="teacher-dashboard-schedule-table">
            <Card className="rounded-2xl">
              <CardContent className="p-0">
                <div className="border-b p-5">
                  <h2 className="text-base font-semibold">{t('teacher.my_classes')}</h2>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('teacher.time')}</TableHead>
                      <TableHead>{t('teacher.course')}</TableHead>
                      <TableHead>{t('teacher.group')}</TableHead>
                      <TableHead>{t('teacher.class_type')}</TableHead>
                      <TableHead>{t('teacher.schedule_type')}</TableHead>
                      <TableHead>{t('teacher.session_type')}</TableHead>
                      <TableHead>{t('teacher.days')}</TableHead>
                      <TableHead>{t('teacher.start_after')}</TableHead>
                      <TableHead>{t('teacher.room')}</TableHead>
                      <TableHead>{t('teacher.students')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schedule.length === 0 && (
                        <TableRow>
                        <TableCell colSpan={10} className="text-sm text-muted-foreground text-center py-6">
                          {t('teacher.no_schedule')}
                        </TableCell>
                      </TableRow>
                    )}
                    {schedule.map((s, i) => (
                      <TableRow
                        key={i}
                        className={`cursor-pointer ${(activeClassReferenceUuid === (s.class_reference_uuid || s.id)) ? 'bg-primary/5' : ''}`}
                        onClick={() => openClassDetails(s.class_reference_uuid || s.id)}
                      >
                        <TableCell className="font-medium text-sm">
                          {s.time}
                          {s.time_table && s.time_table !== s.time ? (
                            <span className="mt-1 block whitespace-pre-line text-xs font-normal text-muted-foreground">
                              {s.time_table}
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-sm">{s.course_title}</TableCell>
                        <TableCell><Badge variant="secondary" className="text-xs">{s.group_name}</Badge></TableCell>
                        <TableCell><ClassTypeBadge value={s.class_type} label={s.class_type_label} t={t} /></TableCell>
                        <TableCell>
                          <Badge variant={s.schedule_type === 'weekly' ? 'default' : 'outline'} className="text-xs">
                            {s.schedule_type_label || (s.schedule_type === 'weekly' ? t('teacher.weekly_schedule') : t('teacher.flexible_schedule'))}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{s.session_location_label || '-'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{s.days || '-'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{s.start_after_date || '-'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{s.room}</TableCell>
                        <TableCell className="text-sm">
                          {s.students_count}
                          {Array.isArray(s.attendees) && s.attendees.length > 0 ? (
                            <span className="block text-xs text-muted-foreground">{s.attendees.join(', ')}</span>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <div className="mt-4">
              {renderClassDetailsPanel()}
            </div>
          </TabsContent>

          <TabsContent value="sessions">
            <div className="space-y-4">
              <Card className="rounded-2xl">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('dashboard.date')}</TableHead>
                        <TableHead>{t('teacher.time')}</TableHead>
                        <TableHead>{t('teacher.course')}</TableHead>
                        <TableHead>{t('teacher.group')}</TableHead>
                        <TableHead>{t('teacher.class_type')}</TableHead>
                        <TableHead>{t('teacher.students')}</TableHead>
                        <TableHead>{t('teacher.type')}</TableHead>
                        <TableHead>{t('dashboard.status')}</TableHead>
                        <TableHead>{t('teacher.class_plan')}</TableHead>
                        <TableHead>{t('teacher.package')}</TableHead>
                        <TableHead className="bf-text-end">{t('teacher.action')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {teacherSessions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11} className="py-6 text-center text-sm text-muted-foreground">
                            {t('teacher.no_generated_sessions_period')}
                          </TableCell>
                        </TableRow>
                      ) : teacherSessions.map((session) => (
                        <TableRow
                          key={session.id}
                          className={`cursor-pointer ${(activeClassReferenceUuid === (session.class_reference_uuid || session.id)) ? 'bg-primary/5' : ''}`}
                          onClick={() => openClassDetails(session.class_reference_uuid || session.id)}
                        >
                          <TableCell className="text-sm">{session.date}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{session.start_time_label} - {session.end_time_label}</TableCell>
                          <TableCell className="text-sm">{session.course}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="text-xs">{session.group || session.student || '-'}</Badge>
                          </TableCell>
                          <TableCell><ClassTypeBadge value={session.class_type} label={session.class_type_label} t={t} /></TableCell>
                          <TableCell>
                            <StudentChips students={session.students} names={session.student_names} />
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{session.session_location_label || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={SESSION_STATE_VARIANTS[session.state] || 'outline'}>
                              {translateSessionState(session.state)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={session.class_plan_status === 'submitted' ? 'default' : 'secondary'}>
                              {translateClassPlanStatus(session.class_plan_status)}
                            </Badge>
                            {session.completion_block_reason ? (
                              <span className="mt-1 block text-xs text-muted-foreground">{session.completion_block_reason}</span>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {Number(session.package_total_sessions || 0) > 0 ? (
                              <span>{packageProgressLabel(session, t)}</span>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="bf-text-end">
                            <div className="bf-justify-end flex gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="rounded-lg"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openClassPlan(session);
                                }}
                              >
                                {t('teacher.class_plan')}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              {renderClassDetailsPanel()}

              {selectedPlanSession ? (
                <Card className="rounded-2xl">
                  <CardContent className="space-y-4 p-5">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h3 className="text-sm font-semibold">{selectedPlanSession.course}</h3>
                        <p className="text-xs text-muted-foreground">
                          {selectedPlanSession.date} - {selectedPlanSession.start_time_label} - {selectedPlanSession.end_time_label}
                        </p>
                      </div>
                      <Badge variant={selectedPlanSession.class_plan_status === 'submitted' ? 'default' : 'secondary'}>
                        {translateClassPlanStatus(selectedPlanSession.class_plan_status)}
                      </Badge>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2">
                      <label className="space-y-1">
                        <span className="text-xs font-medium text-muted-foreground">{t('teacher.lesson_plan')}</span>
                        <textarea
                          value={classPlanForm.lesson_plan}
                          onChange={(event) => updateClassPlanField('lesson_plan', event.target.value)}
                          className="min-h-28 w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-xs font-medium text-muted-foreground">{t('teacher.topics_taught')}</span>
                        <textarea
                          value={classPlanForm.topics_taught}
                          onChange={(event) => updateClassPlanField('topics_taught', event.target.value)}
                          className="min-h-28 w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-xs font-medium text-muted-foreground">{t('teacher.links')}</span>
                        <textarea
                          value={classPlanForm.resource_links}
                          onChange={(event) => updateClassPlanField('resource_links', event.target.value)}
                          className="min-h-24 w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="text-xs font-medium text-muted-foreground">{t('teacher.materials_notes')}</span>
                        <textarea
                          value={classPlanForm.materials_notes}
                          onChange={(event) => updateClassPlanField('materials_notes', event.target.value)}
                          className="min-h-24 w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                        />
                      </label>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <label className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                        <Upload className="h-4 w-4 text-primary" />
                        <span>{t('teacher.upload_files')}</span>
                        <Input
                          type="file"
                          multiple
                          accept=".pdf,.doc,.docx,image/*,audio/*,video/*"
                          className="hidden"
                          onChange={(event) => {
                            addClassPlanFiles(event.target.files);
                            event.target.value = '';
                          }}
                        />
                      </label>
                      <div className="flex flex-1 flex-wrap gap-2">
                        {classPlanForm.attachments?.map((attachment) => (
                          <div key={attachment.id} className="flex items-center gap-2 rounded-full border px-3 py-1 text-xs">
                            <span className="font-medium">{attachment.name}</span>
                            {attachment.uploaded_at ? (
                              <span className="text-muted-foreground">{formatDate(attachment.uploaded_at)}</span>
                            ) : null}
                            <button type="button" onClick={() => downloadClassPlanAttachment(attachment)} className="text-primary hover:underline">
                              <Download className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              className="text-destructive disabled:opacity-50"
                              onClick={() => deleteClassPlanAttachment(attachment.id)}
                              disabled={deletingAttachmentId === attachment.id}
                              aria-label={`${t('teacher.delete_attachment')} ${attachment.name}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                        {classPlanFiles.map((file, index) => (
                          <Badge key={`${file.name}-${index}`} variant="outline" className="gap-1">
                            {file.name}
                            <button type="button" onClick={() => removeSelectedClassPlanFile(index)} aria-label={`${t('teacher.remove_file')} ${file.name}`}>
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                      <Button type="button" className="rounded-lg gap-2" onClick={saveClassPlan} disabled={savingClassPlan}>
                        <Save className="h-4 w-4" /> {t('teacher.save_class_plan')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : null}
            </div>
          </TabsContent>

          <TabsContent value="schedule_requests">
            <Card className="rounded-2xl">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('teacher.student')}</TableHead>
                      <TableHead>{t('teacher.course')}</TableHead>
                      <TableHead>{t('teacher.group')}</TableHead>
                      <TableHead>{t('teacher.students')}</TableHead>
                      <TableHead>{t('teacher.session_type')}</TableHead>
                      <TableHead>{t('teacher.start_after')}</TableHead>
                      <TableHead>{t('teacher.days')}</TableHead>
                      <TableHead>{t('teacher.time')}</TableHead>
                      <TableHead>{t('teacher.sessions_per_week')}</TableHead>
                      <TableHead>{t('dashboard.status')}</TableHead>
                      <TableHead className="bf-text-end">{t('teacher.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scheduleRequests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} className="py-6 text-center text-sm text-muted-foreground">
                          {t('teacher.no_schedule_requests')}
                        </TableCell>
                      </TableRow>
                    ) : scheduleRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell className="text-sm font-medium">
                          {request.request_type === 'group_approval' ? t('teacher.group_approval') : request.student_name}
                        </TableCell>
                        <TableCell className="text-sm">{request.course_name}</TableCell>
                        <TableCell className="text-sm">{request.group_name || '-'}</TableCell>
                        <TableCell>
                          <StudentChips students={request.students} names={request.student_names} />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{request.session_location_label || '-'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{request.schedule_start_date || '-'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{request.preferred_days || '-'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {request.preferred_time || '-'}
                          {request.preferred_schedule_table && request.preferred_schedule_table !== request.preferred_time ? (
                            <span className="mt-1 block whitespace-pre-line text-xs text-muted-foreground">
                              {request.preferred_schedule_table}
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-sm">{request.sessions_per_week || 0}</TableCell>
                        <TableCell>
                          <Badge variant={request.status === 'accepted' ? 'default' : request.status === 'pending_teacher' ? 'secondary' : 'outline'}>
                            {translateScheduleRequestStatus(request.status)}
                          </Badge>
                          {request.teacher_notes ? (
                            <span className="mt-1 block text-xs text-muted-foreground">{request.teacher_notes}</span>
                          ) : null}
                        </TableCell>
                        <TableCell className="bf-text-end">
                          {request.status === 'pending_teacher' || request.status === 'change_requested' ? (
                            <div className="bf-justify-end flex gap-2">
                              <Button
                                size="sm"
                                className="rounded-lg gap-1"
                                onClick={() => acceptScheduleRequest(request.id)}
                                disabled={processingRequestId === request.id}
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" /> {t('teacher.accept')}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="rounded-lg gap-1"
                                onClick={() => requestScheduleChange(request.id)}
                                disabled={processingRequestId === request.id}
                              >
                                <MessageSquare className="h-3.5 w-3.5" /> {t('teacher.request_change')}
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">{t('teacher.no_action_needed')}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="attendance">
            <div className="space-y-6">
              {teacherSessions.filter((session) => !['cancelled', 'postponed', 'holiday'].includes(session.state)).length === 0 && (
                <Card className="rounded-2xl border-dashed">
                  <CardContent className="p-6 text-center">
                    <p className="text-sm font-semibold">{t('teacher.no_attendance_sessions')}</p>
                    <p className="text-xs text-muted-foreground mt-2">{t('teacher.attendance_sessions_hint')}</p>
                  </CardContent>
                </Card>
              )}
              {teacherSessions.filter((session) => !['cancelled', 'postponed', 'holiday'].includes(session.state)).map(session => (
                <Card key={session.id} className="rounded-2xl">
                  <CardContent className="p-5">
                    <div className="flex flex-col gap-4 mb-4 lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <h3 className="font-semibold text-sm">{session.course}</h3>
                        <p className="text-xs text-muted-foreground">
                          {session.date} - {session.start_time_label} - {session.end_time_label} - {session.session_location_label || t('teacher.in_office')}
                        </p>
                        {Number(session.package_total_sessions || 0) > 0 ? (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {packageProgressLabel(session, t)}
                          </p>
                        ) : null}
                      </div>
                      <Button size="sm" className="rounded-lg gap-2" onClick={() => saveAttendance(session)} disabled={savingAttendance || !session.students?.length} data-testid="teacher-dashboard-attendance-save-button">
                        <Save className="h-3.5 w-3.5" /> {t('teacher.save')}
                      </Button>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('teacher.student')}</TableHead>
                          <TableHead>{t('teacher.attendance_status')}</TableHead>
                          <TableHead>{t('teacher.current_value')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(!session.students || session.students.length === 0) ? (
                          <TableRow>
                            <TableCell colSpan={3} className="py-4 text-center text-sm text-muted-foreground">
                              {t('teacher.no_students_linked_session')}
                            </TableCell>
                          </TableRow>
                        ) : session.students.map(s => (
                          <TableRow key={s.id}>
                            <TableCell className="font-medium text-sm">{s.name}</TableCell>
                            <TableCell>
                              <select
                                value={attendance[`${session.id}-${s.id}`] || s.status || 'present'}
                                onChange={(event) => updateAttendanceStatus(session.id, s.id, event.target.value)}
                                className="h-9 rounded-lg border bg-background px-3 text-sm"
                              >
                                <option value="present">{t('teacher.present')}</option>
                                <option value="absent">{t('teacher.absent')}</option>
                                <option value="late">{t('teacher.late')}</option>
                              </select>
                            </TableCell>
                            <TableCell><Badge variant="outline" className="text-xs">{translateAttendanceStatus(s.status)}</Badge></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="hours">
            <Card className="rounded-2xl">
              <CardContent className="p-5">
                <div className="flex flex-col gap-4 border-b pb-5 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <h3 className="font-semibold">{t('teacher.monthly_hours')}</h3>
                    <p className="mt-1 text-xs text-muted-foreground">{t('teacher.salary_period')}</p>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <label className="space-y-1">
                      <span className="text-xs font-medium text-muted-foreground">{t('teacher.date_from')}</span>
                      <Input
                        type="date"
                        value={hoursRange.date_from}
                        onChange={(event) => setHoursRange(prev => ({ ...prev, date_from: event.target.value }))}
                        className="h-10 rounded-lg"
                      />
                    </label>
                    <label className="space-y-1">
                      <span className="text-xs font-medium text-muted-foreground">{t('teacher.date_to')}</span>
                      <Input
                        type="date"
                        value={hoursRange.date_to}
                        onChange={(event) => setHoursRange(prev => ({ ...prev, date_to: event.target.value }))}
                        className="h-10 rounded-lg"
                      />
                    </label>
                    <Button type="button" variant="outline" className="rounded-lg gap-2" onClick={loadHours} disabled={loadingHours}>
                      <FileText className="h-4 w-4" /> {t('teacher.generate_salary_slip')}
                    </Button>
                    <Button type="button" className="rounded-lg gap-2" onClick={printSalarySlip} disabled={loadingHours}>
                      <Printer className="h-4 w-4" /> {t('teacher.print_slip')}
                    </Button>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-2xl border bg-muted/20 p-4">
                    <p className="text-xs text-muted-foreground">{t('teacher.total_hours')}</p>
                    <p className="mt-2 text-2xl font-bold">{hours?.total_hours_month || 0}h</p>
                  </div>
                  <div className="rounded-2xl border bg-muted/20 p-4">
                    <p className="text-xs text-muted-foreground">{t('teacher.total_sessions')}</p>
                    <p className="mt-2 text-2xl font-bold">{hours?.total_classes_month || 0}</p>
                  </div>
                  <div className="rounded-2xl border bg-muted/20 p-4">
                    <p className="text-xs text-muted-foreground">{t('teacher.applied_rates')}</p>
                    {rateBreakdown.length === 0 ? (
                      <p className="mt-2 text-sm text-muted-foreground">{t('teacher.no_completed_sessions')}</p>
                    ) : rateBreakdown.map((item) => (
                      <div key={item.key} className="mt-2">
                        <p className="text-lg font-bold">{item.label} {formatNumber(item.rate)} {t('common.aed_hr')}</p>
                        <p className="text-xs text-muted-foreground">{formatNumber(item.hours)}h / {formatMoney(item.amount, t('common.aed'))}</p>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
                    <p className="text-xs text-muted-foreground">{t('teacher.salary_due')}</p>
                    <p className="mt-2 text-2xl font-bold text-primary">{formatMoney(salaryAmount, t('common.aed'))}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t('teacher.allowance')}: {formatMoney(allowance, t('common.aed'))}
                      {` (${hours?.transport_sessions || 0} ${t('teacher.sessions_lower')})`}
                    </p>
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  {hours?.weekly_breakdown?.map((w, i) => (
                    <div key={i} className="flex items-center gap-4">
                      <span className="text-sm font-medium w-20">{formatDate(w.week)}</span>
                      <div className="flex-1">
                        <div className="h-8 bg-muted rounded-lg overflow-hidden">
                          <div className="h-full bg-primary/20 rounded-lg flex items-center px-3" style={{ width: `${Math.min(100, (w.hours / maxWeeklyHours) * 100)}%` }}>
                            <span className="text-xs font-medium">{w.hours}h / {w.classes} {t('common.classes')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-4 border-t flex items-center justify-between">
                  <span className="text-sm font-medium">{t('teacher.total_month')}</span>
                  <span className="text-lg font-bold text-primary">{hours?.total_hours_month}h / {hours?.total_classes_month} {t('common.classes')}</span>
                </div>

                <div className="mt-6">
                  <h4 className="mb-3 text-sm font-semibold">{t('teacher.session_history')}</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('dashboard.date')}</TableHead>
                        <TableHead>{t('teacher.course')}</TableHead>
                        <TableHead>{t('teacher.group')}</TableHead>
                        <TableHead>{t('teacher.session_type')}</TableHead>
                        <TableHead>{t('teacher.duration_hours')}</TableHead>
                        <TableHead>{t('teacher.rate')}</TableHead>
                        <TableHead>{t('teacher.teaching')}</TableHead>
                        <TableHead>{t('teacher.transportation')}</TableHead>
                        <TableHead>{t('teacher.class_plan')}</TableHead>
                        <TableHead>{t('teacher.students')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(!hours?.sessions || hours.sessions.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={10} className="py-6 text-center text-sm text-muted-foreground">
                            {t('teacher.no_sessions')}
                          </TableCell>
                        </TableRow>
                      )}
                      {hours?.sessions?.map((session, index) => (
                        <TableRow key={`${session.date}-${session.group}-${index}`}>
                          <TableCell className="text-sm">{formatDate(session.date)}</TableCell>
                          <TableCell className="text-sm">{session.course}</TableCell>
                          <TableCell><Badge variant="secondary" className="text-xs">{session.group}</Badge></TableCell>
                          <TableCell className="text-sm text-muted-foreground">{session.session_location_label || '-'}</TableCell>
                          <TableCell className="text-sm">{session.duration_hours}h</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{formatNumber(session.hourly_price)} {t('common.aed_hr')}</TableCell>
                          <TableCell className="text-sm">{formatMoney(session.teaching_subtotal, t('common.aed'))}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{formatMoney(session.transport_amount, t('common.aed'))}</TableCell>
                          <TableCell>
                            <Badge variant={session.class_plan_status === 'submitted' ? 'default' : 'secondary'}>{translateClassPlanStatus(session.class_plan_status)}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            <StudentChips students={session.students} names={session.student_names} />
                            <span className="mt-1 block text-xs">{session.students_present}/{session.students_total}</span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications">
            <Card className="rounded-2xl">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('teacher.type')}</TableHead>
                      <TableHead>{t('teacher.title')}</TableHead>
                      <TableHead>{t('teacher.message')}</TableHead>
                      <TableHead>{t('dashboard.date')}</TableHead>
                      <TableHead className="bf-text-end">{t('teacher.action')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notifications.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                          {t('teacher.no_notifications')}
                        </TableCell>
                      </TableRow>
                    ) : notifications.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell><Badge variant="outline">{item.level || 'info'}</Badge></TableCell>
                        <TableCell className="text-sm font-medium">{item.title}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{item.message}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{item.date}</TableCell>
                        <TableCell className="bf-text-end">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              try {
                                await markDone(item.id);
                                toast.success(t('teacher.notification_cleared'));
                              } catch {
                                toast.error(t('alerts.generic_error'));
                              }
                            }}
                          >
                            {t('teacher.clear')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
